import React, {useEffect, useState} from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {useDispatch, useSelector} from "react-redux";

import useDataSource from "../../../../hooks/useDataSource";
import ClearFilterBtn from "../../../application/ClearFilterBtn";

import {formatIcon} from "../../../../utilities/FormatUtilities";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";
import {dataAddFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";

import Typography from "@mui/material/Typography";
import { LinearProgress } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import {push} from "connected-react-router";


const PREFIX = 'ModelTypeIndicator';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`,
    circleContainer: `${PREFIX}-circleContainer`,
    circleKPIContainer: `${PREFIX}-circleKPIContainer`,
    iconContainer: `${PREFIX}-iconContainer`,
    icon: `${PREFIX}-icon`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    },

    [`& .${classes.circleContainer}`]: {
        width: theme.spacing(10),
        height: theme.spacing(10),
        borderRadius: '50%',
        display: 'block',
        position: 'absolute',
        left: '50%',
        top: '50%',
        margin: '-35px 0 0 -35px',
        color: 'black',
		cursor: 'pointer'
        // backgroundColor: '#00e676'
    },

    [`& .${classes.circleKPIContainer}`]: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        borderRadius: '50%',
        color: 'black'
    },

    [`& .${classes.iconContainer}`]: {
        height: '10%',
        width: '100%',
        textAlign: 'center'
    },

    [`& .${classes.icon}`]: {
		fontSize: '24px',
		verticalAlign: 'middle',
		// marginTop: '10px'
		height: '100%'
	}
}));

export default function ModelTypeIndicator({id, configuration, xId, dataSourceIndex, onLoading, onLoaded}) {

	const dispatch = useDispatch();
	const { t, i18n } = useTranslation();

    // App State
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [value, setValue] = useState(null);
	const [link, setLink] = useState("#");
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [filterValue, setFilterValue] = useState(null);

	const dataSourceName = compConfig.dataSource?.name !== undefined ? compConfig.dataSource?.name : '';
	const xFilter = compConfig?.xFilter?.enabled !== undefined ? (compConfig.xFilter.enabled === true ? compConfig.xFilter : undefined) : undefined;
	const xFilters = compConfig?.xFilters?.enabled !== undefined ? (compConfig.xFilters.enabled === true ? compConfig.xFilters : undefined) : undefined;
	const element = compConfig.element ? compConfig.element : {};
	const styles = compConfig?.element?.styles;
	const dataIndex = dataSourceIndex !== undefined ? dataSourceIndex : 0;

	let dataSource = useDataSource(dataSourceName);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Model Type Indicator Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'ModelTypeIndicator'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    // Data
    useEffect(() => {

		let data =[];
		data = dataSource.data;

		if (dataSource.error) {
			setValue(null);
		}
		else if (data && dataSource.fetched) {
			// Data is greater than 1 AND the index is less tha the zero (0) based length
			if (data.length > 1 && dataIndex < data.length) {
				setTitle(data[dataIndex][element.titleFields.title]);
				setSubTitle(data[dataIndex][element.titleFields.subtitle]);
				setValue(formatIcon(data[dataIndex][compConfig.dataSource.icon], {fontSize: '50px'}));
				setFilterValue(xFilter ? data[dataIndex][xFilter.field] : null);
				setLink(element.link);
			} else if (data.length === 1) {
				setTitle(data[0][element.titleFields.title]);
				setSubTitle(data[0][element.titleFields.subtitle]);
				setValue(formatIcon(data[0][compConfig.dataSource.icon], {fontSize: '50px'}));
				setFilterValue(xFilter ? data[0][xFilter.field] : null);
				setLink(element.link);
			}
		}

    }, [dataSource.data]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	const handleClick = (value) => {
		if (xFilter !== undefined) {
			if (xFilter.enabled === true) {
				let filterFieldToAdd = xFilter.field;
				let filterValueToAdd = value;

				if (xFilter.operator.toLowerCase() === 'like') {
					dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
				} else {
					dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
				}

				if (xFilters !== undefined) {
					if (xFilters.enabled === true) {
						for (let i = 0; i < xFilters.fields.length; i++) {
							let filterFieldToAdd = xFilters.fields[i];
							let filterValueToAdd = dataSource.data[dataIndex][filterFieldToAdd];

							if (xFilter.operator.toLowerCase() === 'like') {
								dispatch(dataAddFilter(dataSourceName + "-" + filterFieldToAdd, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
							} else {
								dispatch(dataAddFilter(dataSourceName + "-" + filterFieldToAdd, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
							}
						}
					}
				}

				dispatch(push('{0}'.format(link)));
			}
		}
	}

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'hidden'
			,padding: '0px'
			//,display: 'flex'
			,flexDirection: 'column'
			,justifyContent: 'center'
		});

		if (title && subtitle) {
			// Default Style
			newCardHeaderStyle.height = '72px';
			newCardContentStyle.height = 'calc(100% - 72px)';
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 12px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 12px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', ...styles?.indicator}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{variant: 'subtitle2', color: 'textPrimary'}}
                            title={t(title)}
                            subheaderTypographyProps={{variant: 'body2', color: 'textSecondary'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <Typography variant="h5" gutterBottom align="center" style={{position: 'relative', top: '0px', width: '100%', height: '100%', marginTop: '3px'}}>
						<div style={{display: 'inline-flex', alignItems: 'center'}}>
							<Tooltip title={t(subtitle)} arrow placement="bottom">
								<Avatar className={classes.circleContainer} onClick={() =>{ handleClick(filterValue) } }>
									<div className={classes.iconContainer} style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
										{value}
									</div>
								</Avatar>
							</Tooltip>
						</div>
                    </Typography>
                </CardContent>
            </Card>
        </Root>
    );
}
